import { useTranslation } from 'react-i18next';
import { Icon } from '../../components/Icon';
import type { SearchLike } from '../../hooks/searchRequestState';
import type { SearchResults } from '@peruselab/api-client/moleculeSearch';

interface MatchCountBadgeProps {
  readonly search: SearchLike<SearchResults>;
  readonly visibleCount: number;
  /** Matches hidden by the Filter tab's settings. */
  readonly hiddenCount: number;
  /** #932 -- copies of a structure folded into the cards shown (drawings, illustrations). */
  readonly foldedCount?: number;
  /** Opens the Filter tab -- offered only while some matches are hidden. */
  readonly onShowFilters: () => void;
  /** #701 -- set once the results have expired (answers are kept 60 s): fetches them afresh. */
  readonly onRefresh?: (() => void) | undefined;
}

// #491 -- the match count used to live in SearchStatusHeader, alone in a full-width status row
// above the results grid. Moved next to the formula pill in the toolbar instead: "how many
// results match what's shown right now" reads more naturally paired with "what's shown right
// now" (the formula) than floating above a grid of result cards. Visible whenever there are
// results, regardless of staleness -- the same condition SearchStatusHeader used to gate on.
// #680 -- when filters hide some matches it reads "51 of 124 matches" and opens the Filter tab;
// that replaces the "N matches hidden by filters." note, which took a whole result cell.
export function MatchCountBadge({
  search,
  visibleCount,
  hiddenCount,
  foldedCount = 0,
  onShowFilters,
  onRefresh
}: MatchCountBadgeProps) {
  const { t } = useTranslation();
  if (search.currentResults === null) return null;
  // #932 -- what was folded, and the rule (one card per molecule; its copies behind its icons).
  const folded =
    foldedCount > 0 ? (
      <span className="workspace-match-folded" data-testid="match-folded" title={t('workspace.documentBar.foldedRule')}>
        {t('workspace.documentBar.foldedIn', { count: foldedCount })}
        <span className="visually-hidden">. {t('workspace.documentBar.foldedRule')}</span>
      </span>
    ) : null;
  const refresh = onRefresh ? (
    <button
      type="button"
      className="icon-button workspace-match-refresh"
      data-testid="match-count-refresh"
      aria-label={t('workspace.documentBar.refreshResults')}
      title={t('workspace.documentBar.refreshResults')}
      onClick={onRefresh}
    >
      <Icon name="refresh" size="1.1rem" />
    </button>
  ) : null;

  if (hiddenCount === 0) {
    return (
      <>
        <span className="workspace-match-count" data-testid="match-count-badge">
          {t('workspace.documentBar.matchCount', { count: visibleCount })}
        </span>
        {folded}
        {refresh}
      </>
    );
  }

  const total = visibleCount + hiddenCount;
  const label = t('workspace.documentBar.matchCountOf', { visible: visibleCount, count: total });
  const hint = t('workspace.documentBar.hiddenByFilters', { count: hiddenCount });
  return (
    <>
      <button
        type="button"
        className="workspace-match-count workspace-match-count--filtered"
        data-testid="match-count-badge"
        title={hint}
        aria-label={`${label}. ${hint}`}
        onClick={onShowFilters}
      >
        {label}
      </button>
      {folded}
      {refresh}
    </>
  );
}
